import { Replace } from '@/assets/icons/ats';
import { useShowToast } from '@/hooks';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { Flex, useDisclosure, Icon, Spinner } from '@chakra-ui/react';
import { Download01 } from '@kula-ai/icons/dual-tone/Download01';
import { Eye } from '@kula-ai/icons/dual-tone/Eye';
import { File04 } from '@kula-ai/icons/dual-tone/File04';
// import { Trash01 } from '@kula-ai/icons/dual-tone/Trash01';
import { useRef, useState } from 'react';
import { IconButton } from '../IconButton';
import { PreviewPdfModal } from './PreviewPdfModal';
import { XClose } from '@kula-ai/icons/solid/XClose';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';

export const ViewFile = ({
  file,
  showDelete = false,
  handleDelete = () => {},
  primaryColor = '',
  showDownload = true,
  showPreview = true,
  showReplace = false,
  handleUpload = () => {},
  acceptedFiles = '',
  showIcon = false,
  hideTextReference = false,
  containerProps = {},
  textContainerProps = {},
  useDirect = false,
  isTransferred = false,
  transferInfo = null,
  maxW = '380px',
}) => {
  const {
    file_name = '',
    url,
    content_type,
    filename,
    preview,
    name,
    type,
  } = file;
  const toast = useShowToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const attachFileRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    const cleanup = () => setIsDownloading(false);

    downloadFileFromUrl({
      filename: file_name || filename || name,
      url: file instanceof File ? URL.createObjectURL(file) : url,
      useDirect,
      onSuccess: () => {
        toast({ title: `File downloaded` });
        cleanup();
      },
      onError: (_error) => {
        toast({ title: 'Failed to download', status: 'error' });
        cleanup();
      },
    });
  };

  return (
    <>
      <Flex
        border="1px solid"
        borderRadius="6px"
        borderColor="border-color"
        p="12px 16px"
        justifyContent="space-between"
        alignItems="center"
        gap="12px"
        {...containerProps}
      >
        <Flex
          alignItems="center"
          gap="12px"
          flex={1}
          minW={0}
          {...textContainerProps}
        >
          {showIcon && (
            <Flex
              bg="secondary-50"
              p="8px"
              borderRadius="8px"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={File04} boxSize="24px" />
            </Flex>
          )}
          <TruncatedTextWithTooltip
            size="sm-medium"
            maxW={maxW}
            text={file_name || filename || name}
            enableTooltip={true}
          />
          {isTransferred && transferInfo && (
            <TransferredBadge transferInfo={transferInfo} />
          )}
        </Flex>
        <Flex gap="12px" flexShrink={0}>
          {(content_type === 'application/pdf' ||
            preview?.url ||
            type === 'application/pdf') &&
            showPreview && (
              <IconButton
                text={!hideTextReference ? 'Preview' : ''}
                icon={Eye}
                iconContainerProps={{
                  width: 'unset',
                  height: 'unset',
                  bg: 'unset',
                }}
                iconProps={{
                  width: '18px',
                  height: '18px',
                  color: primaryColor ? primaryColor : 'ats-primary',
                }}
                gap="4px"
                onClick={onOpen}
                type="button"
                textProps={{
                  color: primaryColor ? primaryColor : 'ats-primary',
                }}
              />
            )}
          {showDownload && (
            <IconButton
              text={!hideTextReference && !isDownloading ? 'Download' : ''}
              icon={isDownloading ? Spinner : Download01}
              iconContainerProps={{
                width: 'unset',
                height: 'unset',
                bg: 'unset',
              }}
              iconProps={{
                width: '18px',
                height: '18px',
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
              gap="4px"
              onClick={handleDownload}
              type="button"
              disabled={isDownloading}
              textProps={{ color: primaryColor ? primaryColor : 'ats-primary' }}
            />
          )}
          {showReplace && (
            <Flex onClick={() => attachFileRef?.current?.click()}>
              <input
                ref={attachFileRef}
                type="file"
                accept={acceptedFiles.join(', ')}
                onChange={(event) => {
                  if (event?.target?.files?.length) {
                    handleUpload(Array.from(event?.target?.files));
                  }
                }}
                max={1}
                style={{ visibility: 'hidden', height: 0, width: 0 }}
              />
              <IconButton
                text="Replace"
                icon={Replace}
                iconContainerProps={{
                  width: 'unset',
                  height: 'unset',
                  bg: 'unset',
                }}
                iconProps={{
                  width: '18px',
                  height: '18px',
                  color: primaryColor ? primaryColor : 'ats-primary',
                }}
                gap="4px"
                type="button"
                textProps={{
                  color: primaryColor ? primaryColor : 'ats-primary',
                }}
              />
            </Flex>
          )}
          {showDelete && (
            <IconButton
              text={!hideTextReference ? 'Delete' : ''}
              icon={XClose}
              iconContainerProps={{
                width: 'unset',
                height: 'unset',
                bg: 'unset',
              }}
              iconProps={{
                width: '18px',
                height: '18px',
                color: primaryColor ? primaryColor : 'ats-primary',
              }}
              gap="4px"
              onClick={handleDelete}
              type="button"
              textProps={{ color: primaryColor ? primaryColor : 'ats-primary' }}
            />
          )}
        </Flex>
      </Flex>
      {isOpen && (
        <PreviewPdfModal isOpen={isOpen} onClose={onClose} file={file} />
      )}
    </>
  );
};
